import type { AssetRopaReviewListParams } from 'config/api/ropaReviews/ropaReviews.types';

import { appendDpiaFilterQueryParams } from './assetRopaDpiaFilters';
import type {
  AssetRopaBooleanFilterValue,
  AssetRopaFilterPaginationOptions,
  AssetRopaReviewFilters,
  OwnerWorkspaceFilters,
} from './assetRopaFilter.types';
import type { AssetRopaOwnerFilterParams } from './assetRopaOwnerFilter.types';

export const mapBooleanFilterValue = (value: AssetRopaBooleanFilterValue): boolean | undefined => {
  if (value === 'true') {
    return true;
  }

  if (value === 'false') {
    return false;
  }

  return undefined;
};

export const appendPaginationParams = (
  options: AssetRopaFilterPaginationOptions,
): Pick<AssetRopaOwnerFilterParams, 'page' | 'page_size'> => ({
  ...(options.page !== undefined ? { page: options.page } : {}),
  ...(options.pageSize !== undefined ? { page_size: options.pageSize } : {}),
});

export const appendSharedRegisterFilterParams = (
  filters: Pick<AssetRopaReviewFilters, 'search' | 'assetTypeId' | 'sensitivePersonalData' | 'thirdCountryProcessing'>,
): Pick<AssetRopaOwnerFilterParams, 'search' | 'asset_type' | 'sensitive_data' | 'third_country'> => {
  const sensitiveData = mapBooleanFilterValue(filters.sensitivePersonalData);
  const thirdCountry = mapBooleanFilterValue(filters.thirdCountryProcessing);

  return {
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.assetTypeId ? { asset_type: filters.assetTypeId } : {}),
    ...(sensitiveData !== undefined ? { sensitive_data: sensitiveData } : {}),
    ...(thirdCountry !== undefined ? { third_country: thirdCountry } : {}),
  };
};

export const ownerWorkspaceFiltersToQueryParams = (
  filters: OwnerWorkspaceFilters,
  options: AssetRopaFilterPaginationOptions = {},
): AssetRopaOwnerFilterParams =>
  appendDpiaFilterQueryParams(
    {
      ...appendPaginationParams(options),
      ...appendSharedRegisterFilterParams(filters),
      ...(filters.completeness ? { completeness: filters.completeness } : {}),
      ...(filters.gdprRelevance !== 'all' ? { gdpr_relevance: filters.gdprRelevance } : {}),
    },
    filters,
  );

export const assetRopaReviewRegisterFiltersToQueryParams = (
  filters: AssetRopaReviewFilters,
  options: AssetRopaFilterPaginationOptions = {},
): AssetRopaOwnerFilterParams =>
  appendDpiaFilterQueryParams(
    {
      ...appendPaginationParams(options),
      ...appendSharedRegisterFilterParams(filters),
    },
    filters,
  );

/** Query params for `GET .../ropa-reviews/{round}/assets/` (review-round triage filters). */
export const assetRopaReviewRoundFiltersToQueryParams = (
  filters: AssetRopaReviewFilters,
  options: AssetRopaFilterPaginationOptions = {},
): AssetRopaReviewListParams => {
  const sensitiveData = mapBooleanFilterValue(filters.sensitivePersonalData);
  const thirdCountryProcessing = mapBooleanFilterValue(filters.thirdCountryProcessing);

  return appendDpiaFilterQueryParams(
    {
      ...appendPaginationParams(options),
      ...(filters.search ? { search: filters.search } : {}),
      ...(filters.reviewStatus ? { review_status: filters.reviewStatus } : {}),
      ...(filters.assetTypeId ? { asset_type: filters.assetTypeId } : {}),
      ...(filters.operationalOwnerMembershipId ? { operational_owner: filters.operationalOwnerMembershipId } : {}),
      ...(filters.warningState === 'with_warnings' ? { has_warnings: true } : {}),
      ...(filters.warningState === 'no_warnings' ? { has_warnings: false } : {}),
      ...(sensitiveData !== undefined ? { sensitive_data: sensitiveData } : {}),
      ...(thirdCountryProcessing !== undefined ? { third_country_processing: thirdCountryProcessing } : {}),
    },
    filters,
  );
};
